import { pow, log10, round, format, ceil, floor } from "mathjs"
import { datesToHuman } from "@/utils/dates.js";

export const blobToBase64 = blob => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);

  return new Promise(resolve => {
    reader.onloadend = () => {
      resolve(reader.result);
    };
  });
};

export const base64ToBlob = async base64 => {
  return new Promise(resolve => {
    fetch(base64).then(r => {
      resolve(r.blob())
    });
  })
};

export const objectImagesConvert = images => {
  return images.map(item => {
    const state = { ...item.polygons }
    state.image = item.image_file
    delete item.line

    return {
      state: state,
      id: item.id,
      uuid: item.id,
      label: item.label,
      vistat_object: item.vistat_object
    }
  })
}

export const getBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

const getNumberCount = n => {
  if (n >= 1) {
    return ceil(log10(n + 1));
  } else if (n > 0 && n < 1) {
    // Для дробных чисел используем floor(log10(n)) чтобы получить правильный порядок
    return floor(log10(n));
  } else {
    return ceil(log10(n + 1));
  }
};


export const intToHumanReadable = (number) => {
  const isNegativeValue = parseFloat(number) < 0
  const parsedInt = isNegativeValue ? Math.abs(parseFloat(number)) : parseFloat(number)

  if (Number.isNaN(parsedInt)) return number

  const numberCount = getNumberCount(parsedInt)
  const hasMinus = isNegativeValue ? '-' : ''

  if (number.toString().includes('e')) {
    const numToArr = number.toString().split('e')
    if (numToArr[1] < 0) {
      return `${numToArr[0]}⋅10<sup>${numToArr[1]}</sup>`
    }
  }

  if (parsedInt >= 1) {
    // до 6 знаков в основании
    if (numberCount <= 6) {
      return hasMinus + parsedInt.toLocaleString(undefined, { maximumFractionDigits: 3 })
    } else if (numberCount <= 9) {
      return `${hasMinus + (parsedInt / 1000000).toLocaleString(undefined, { maximumFractionDigits: 3 })} млн`
    } else if (numberCount <= 12) {
      return `${hasMinus + (parsedInt / 1000000000).toLocaleString(undefined, { maximumFractionDigits: 3 })} млрд`
    } else if (numberCount <= 15) {
      return `${hasMinus + (parsedInt / 1000000000000).toLocaleString(undefined, { maximumFractionDigits: 3 })} трлн`
    } else {
      return `${hasMinus + (parsedInt / (pow(10, numberCount - 1))).toLocaleString(undefined, { maximumFractionDigits: 3 })}⋅10<sup>${numberCount - 1}</sup>`
    }
  } else if (parsedInt > 0 && parsedInt < 1) {
    if (numberCount >= -5) {
      return hasMinus + parsedInt.toLocaleString(undefined, { maximumFractionDigits: 5 })
    } else {
      return `${hasMinus + (parsedInt / (pow(10, numberCount))).toLocaleString(undefined, { maximumFractionDigits: 3 })}⋅10<sup>${numberCount}</sup>`
    }
  }

  return number
}

export const eToNumber = (num) => {
  const parsed = parseFloat(num)
  if (Number.isNaN(parsed)) return num

  if (process.client) {
    return format(parsed, {
      notation: 'fixed',
      lowerExp: -50,
      upperExp: 100,
    });
  }
}


/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export function humanFileSize(bytes, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);


  return bytes.toFixed(dp) + ' ' + units[u];
}

const isSiType = (prop) => {
  return Boolean(prop.value?.type === 'number' && prop.value.si) || Boolean(prop.template?.value?.type === "number" && prop.template.value_si)
}
const siType = (prop) => {
  return prop.value?.si?.si || prop.value_si_info?.si || prop.template.value_si_info?.si
}

const getObjectInfo = (id, objectsGetter) => {
  return objectsGetter.find(x => x.key === id)
}

/**
 *
 * @param prop
 * @param objectsGetter
 * @param options
 * @param options.reverseFullObject {Boolean}
 * @return {*[][]|(string|*)[][]|*|string[][]|*[][]}
 */
export const humanReadablePropValue = (prop, objectsGetter, options = {}) => {
  try{
    options = {
      reverseFullObject: true,
      ...options
    }
    if (prop.value?.type === 'number' || prop.template.value?.type === 'number') {
      let val = prop.value?.type === 'number' ? prop.value.value : prop.value
      let humanValue = ''
      if (typeof val === 'string' || typeof val === 'number') {
        humanValue = intToHumanReadable(prop.value)
      } else if (val[0] === val[1]) {
        humanValue = intToHumanReadable(val[0])
      } else {
        if (val[0] >= 0 && val[1] >= 0) {
          humanValue = `${intToHumanReadable(val[0])} - ${intToHumanReadable(val[1])}`
        } else {
          humanValue = `${intToHumanReadable(val[0])}...${intToHumanReadable(val[1])}`
        }
      }
      return [[`${humanValue}${isSiType(prop) ? ' ' + siType(prop) : ''}`]]
    } else if (prop.value?.type === 'date' || prop.template.value?.type === 'date') {
      return [[datesToHuman(
        {
          value: prop.date?.vfrom,
          approx: prop.date?.vfrom_approx
        },
        {
          value: prop.date.vto,
          approx: prop.date?.vto_approx
        })]]
    } else if (prop.value?.type === 'select' || prop.template.value?.type === 'select') {
      if (prop.template?.value?.type === 'select') {
        return prop.value.map(x => {
          const option = prop.template.value.options.find(e => e.key === x)
          return [option.value, option.vistat_object]
        })
      } else {
        return prop.value?.value_annotated.map(x => {
          return [x.value, x.vistat_object]
        })
      }

    } else if (prop.value?.type === 'annotated-object') {
      const result = (Array.isArray(prop.value.value) ? prop.value.value : [prop.value.value])
        .map(id => {
          const ANNOTATED = prop.value.value_annotated.find(x => x.id === id)
          const LABEL = ANNOTATED.short_label || ANNOTATED.label
          if (options.reverseFullObject) return [LABEL, ANNOTATED.slug]
          return [LABEL]
        })
      return result.length ? result : [["Нет данных"]]
    } else if (prop.template.value?.type === 'object') {
      const result = (Array.isArray(prop.value) ? prop.value : [prop.value])
        .map(x => {
          const info = getObjectInfo(x, objectsGetter)
          const LABEL = info?.raw?.short_label || info?.label
          if (options.reverseFullObject) return [LABEL, x]
          else return [LABEL]
        })
        .filter(x => x[0])

      return result.length ? result : [["Нет данных"]]
    } else if (prop.template.value?.type === 'plain' && Array.isArray(prop.value)) {
      return [[prop.value.join('')]]
    }
    return [[prop.value]]

  }
  catch (e) {
    console.error(e);
    return '-'
  }
}

export const computeRenderIconOfProp = (prop) => {
  let renderCollectIcon = null

  if (prop.template?.value?.type === 'select') {
    const matchOptionsWithIcon = prop.template.value.options.filter(x => prop.value.includes(x.key) && x.icon)
    if (matchOptionsWithIcon.length === 1) renderCollectIcon = matchOptionsWithIcon[0].icon
  }

  if (prop.value?.type === 'select') {
    const matchOptionsWithIcon = prop.value.value_annotated.filter(x => x.icon)
    if (matchOptionsWithIcon.length === 1) renderCollectIcon = matchOptionsWithIcon[0].icon
  }

  if (renderCollectIcon === null) {
    if (typeof prop?.template_annotated?.icon === 'string') {
      renderCollectIcon = prop.template_annotated.icon
    } else {
      renderCollectIcon = prop.template?.icon?.id ?? prop.template?.icon
    }
  }

  return renderCollectIcon
}

export const newPropToOld = (data, templates) => {
  const prop = JSON.parse(JSON.stringify(data))

  //prop.template = JSON.parse(JSON.stringify(templates[prop.template_id]))

  if (prop.value.type === 'date') {
    prop.date = {
      vfrom: prop.value.value.vfrom.value,
      vfrom_approx: prop.value.value.vfrom.is_approx,
      vto: prop.value.value.vto?.value ? prop.value.value.vto : null,
      vto_approx: prop.value.value.vto?.is_approx ? prop.value.value.vto.is_approx : null,
    }
  } else if (prop.value.type === 'object') {
    prop.value.type = 'annotated-object'
  }

  return prop
}
